import React, { PropsWithChildren, ReactElement, useContext } from 'react';

import PropTypes from 'prop-types';
import { HeaderGroup } from 'react-table';
import { CSSObject } from 'styled-components';

import { stylePropType, StylableComponent } from '@adsk/alloy-react-helpers';

import TablePropTypes from './propTypes';
import TableHeaderCell from './TableHeaderCell';
import TableSizeThemeContext from './TableSizeThemeContext';

export type TableHeaderRowProps<D extends Record<string, unknown>> =
  StylableComponent<HTMLDivElement> & {
    headerGroup: HeaderGroup<D>;
    isPrimaryRow?: boolean;
    isResizingAnyColumn?: boolean;
  };

const TableHeaderRow = <D extends Record<string, unknown>>({
  headerGroup,
  children,
  isPrimaryRow,
  isResizingAnyColumn,
  style = {},
  ...props
}: PropsWithChildren<TableHeaderRowProps<D>>): ReactElement => {
  const { tableSizeTheme } = useContext(TableSizeThemeContext);
  return (
    <div
      {...props}
      css={[
        {
          width: '100%',
          minWidth: 'fit-content',
          position: 'relative',
          height: tableSizeTheme.rowHeight,
        },
        style,
        { minWidth: 'fit-content' }, // Overwrite `style`'s min-width too fix sticky column
      ]}
    >
      {children
        ? React.Children.map(children, (child, index) => {
            const column = headerGroup.headers[index];
            return React.cloneElement(child as ReactElement, {
              column,
            });
          })
        : headerGroup.headers.map((column) => {
            const { style, ...headerProps } = column.getHeaderProps();
            return (
              <TableHeaderCell<D>
                {...headerProps}
                style={style as CSSObject}
                key={headerProps.key}
                column={column}
                isPrimaryRow={isPrimaryRow}
                isResizingAnyColumn={isResizingAnyColumn}
              />
            );
          })}
    </div>
  );
};

TableHeaderRow.propTypes = {
  style: stylePropType,
  children: PropTypes.node,
  headerGroup: TablePropTypes.headerGroup.isRequired,
  isPrimaryRow: PropTypes.bool,
};

export default TableHeaderRow;
